import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { TextArea } from '../components/ui/TextArea';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Checkbox } from '../components/ui/Checkbox';
import { Company, TermsAndConditions } from '../types';
import { formatPercent, formatDate } from '../utils/calculations';
import { BuildingIcon, KeyIcon, PlusIcon, EditIcon, TrashIcon, FileTextIcon } from 'lucide-react';
export function Settings() {
  const {
    companies,
    taxRate,
    setTaxRate,
    selectedCompanyId,
    getTermsByCompanyId,
    createTerms,
    updateTerms,
    deleteTerms
  } = useData();
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [editingTerms, setEditingTerms] = useState<TermsAndConditions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // Terms form state
  const [termsName, setTermsName] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [termsErrors, setTermsErrors] = useState<Record<string, string>>({});
  const companyTerms = getTermsByCompanyId(selectedCompanyId);
  const openCreateTermsModal = () => {
    setEditingTerms(null);
    setTermsName('');
    setTermsContent('');
    setIsDefault(companyTerms.length === 0); // First one is default
    setTermsErrors({});
    setIsTermsModalOpen(true);
  };
  const openEditTermsModal = (terms: TermsAndConditions) => {
    setEditingTerms(terms);
    setTermsName(terms.name);
    setTermsContent(terms.content);
    setIsDefault(terms.is_default);
    setTermsErrors({});
    setIsTermsModalOpen(true);
  };
  const validateTermsForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!termsName.trim()) {
      errors.name = 'Name is required';
    }
    if (!termsContent.trim()) {
      errors.content = 'Content is required';
    }
    setTermsErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSaveTerms = async () => {
    if (!validateTermsForm()) return;
    setIsLoading(true);
    try {
      if (editingTerms) {
        await updateTerms(editingTerms.id, {
          name: termsName,
          content: termsContent,
          is_default: isDefault
        });
      } else {
        await createTerms({
          company_id: selectedCompanyId,
          name: termsName,
          content: termsContent,
          is_default: isDefault
        });
      }
      setIsTermsModalOpen(false);
    } catch (error) {
      console.error('Failed to save terms:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteTerms = async (id: string) => {
    setIsLoading(true);
    try {
      await deleteTerms(id);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete terms:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const companyColumns = [{
    key: 'name',
    header: 'Company Name',
    render: (company: Company) => <div className="flex items-center gap-3" data-id="element-547">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-548">
            <BuildingIcon className="w-5 h-5 text-blue-600" data-id="element-549" />
          </div>
          <span className="font-medium text-gray-900" data-id="element-550">{company.name}</span>
        </div>
  }, {
    key: 'id',
    header: 'Company ID',
    render: (company: Company) => <Badge variant="default" data-id="element-551">{company.id}</Badge>
  }, {
    key: 'stripe_key',
    header: 'Stripe Key',
    render: (company: Company) => <div className="flex items-center gap-2" data-id="element-552">
          <KeyIcon className="w-4 h-4 text-gray-400" data-id="element-553" />
          <code className="text-sm bg-gray-100 px-2 py-1 rounded" data-id="element-554">
            {company.stripe_key.slice(0, 12)}...
          </code>
        </div>
  }];
  const termsColumns = [{
    key: 'name',
    header: 'Name',
    render: (terms: TermsAndConditions) => <div className="flex items-center gap-2" data-id="element-555">
          <FileTextIcon className="w-4 h-4 text-gray-400" data-id="element-556" />
          <span className="font-medium text-gray-900" data-id="element-557">{terms.name}</span>
          {terms.is_default && <Badge variant="info" className="ml-2" data-id="element-558">
              Default
            </Badge>}
        </div>
  }, {
    key: 'updated',
    header: 'Last Updated',
    render: (terms: TermsAndConditions) => <span className="text-sm text-gray-500" data-id="element-559">
          {formatDate(terms.updated_at)}
        </span>
  }, {
    key: 'actions',
    header: '',
    render: (terms: TermsAndConditions) => <div className="flex items-center justify-end gap-2" data-id="element-560">
          <Button variant="ghost" size="sm" onClick={() => openEditTermsModal(terms)} aria-label="Edit terms" data-id="element-561">
            <EditIcon className="w-4 h-4" data-id="element-562" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(terms.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Delete terms" disabled={terms.is_default} data-id="element-563">
            <TrashIcon className="w-4 h-4" data-id="element-564" />
          </Button>
        </div>
  }];
  return <div className="space-y-6" data-id="element-565">
      {/* Page Header */}
      <div data-id="element-566">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-567">Settings</h1>
        <p className="text-gray-500 mt-1" data-id="element-568">
          Manage your billing system configuration
        </p>
      </div>

      {/* Tax Settings */}
      <Card data-id="element-569">
        <CardHeader title="Tax Settings" subtitle="Configure default tax rate for invoices" data-id="element-570" />
        <div className="max-w-xs" data-id="element-571">
          <Input label="Default Tax Rate" type="number" min="0" max="1" step="0.01" value={taxRate} onChange={e => setTaxRate(parseFloat(e.target.value) || 0)} helperText={`Current rate: ${formatPercent(taxRate)} (Michigan default: 6%)`} data-id="element-572" />
        </div>
      </Card>

      {/* Terms & Conditions */}
      <Card padding="none" data-id="element-573">
        <div className="p-6 border-b border-gray-200" data-id="element-574">
          <CardHeader title="Terms & Conditions" subtitle="Manage terms and conditions templates for quotes" action={<Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-577" />} onClick={openCreateTermsModal} data-id="element-576">
                Add Terms
              </Button>} data-id="element-575" />
        </div>
        <Table columns={termsColumns} data={companyTerms} keyExtractor={t => t.id} emptyMessage="No terms and conditions created yet" data-id="element-578" />
      </Card>

      {/* Companies */}
      <Card padding="none" data-id="element-579">
        <div className="p-6 border-b border-gray-200" data-id="element-580">
          <CardHeader title="Companies" subtitle="View configured companies and their Stripe keys" data-id="element-581" />
        </div>
        <Table columns={companyColumns} data={companies} keyExtractor={c => c.id} data-id="element-582" />
      </Card>

      {/* System Info */}
      <Card data-id="element-583">
        <CardHeader title="System Information" data-id="element-584" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" data-id="element-585">
          <div data-id="element-586">
            <span className="text-gray-500" data-id="element-587">Version</span>
            <p className="font-medium text-gray-900" data-id="element-588">1.0.0</p>
          </div>
          <div data-id="element-589">
            <span className="text-gray-500" data-id="element-590">Environment</span>
            <p className="font-medium text-gray-900" data-id="element-591">Test Mode</p>
          </div>
          <div data-id="element-592">
            <span className="text-gray-500" data-id="element-593">Stripe Fee</span>
            <p className="font-medium text-gray-900" data-id="element-594">2.9% + $0.30</p>
          </div>
          <div data-id="element-595">
            <span className="text-gray-500" data-id="element-596">Data Storage</span>
            <p className="font-medium text-gray-900" data-id="element-597">Local Storage</p>
          </div>
        </div>
      </Card>

      {/* Terms Modal */}
      <Modal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} title={editingTerms ? 'Edit Terms & Conditions' : 'Add Terms & Conditions'} size="lg" data-id="element-598">
        <div className="space-y-4" data-id="element-599">
          <Input label="Name" value={termsName} onChange={e => setTermsName(e.target.value)} error={termsErrors.name} placeholder="e.g., Standard Terms, Service Agreement" data-id="element-600" />

          <TextArea label="Content" value={termsContent} onChange={e => setTermsContent(e.target.value)} error={termsErrors.content} placeholder="Enter your terms and conditions..." className="min-h-[200px]" helperText="You can use basic HTML formatting" data-id="element-601" />

          <Checkbox label="Set as default terms" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} data-id="element-602" />

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-603">
            <Button variant="secondary" onClick={() => setIsTermsModalOpen(false)} data-id="element-604">
              Cancel
            </Button>
            <Button onClick={handleSaveTerms} isLoading={isLoading} data-id="element-605">
              {editingTerms ? 'Save Changes' : 'Add Terms'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Terms & Conditions" size="sm" data-id="element-606">
        <p className="text-gray-600 mb-6" data-id="element-607">
          Are you sure you want to delete these terms? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-3" data-id="element-608">
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)} data-id="element-609">
            Cancel
          </Button>
          <Button variant="danger" onClick={() => deleteConfirmId && handleDeleteTerms(deleteConfirmId)} isLoading={isLoading} data-id="element-610">
            Delete
          </Button>
        </div>
      </Modal>
    </div>;
}